import api, { queryClient } from "@app/config/api/api";
import type { ExperimentId } from "@globals";
import { notifications } from "@lib/notification/notification";
import { useQuery } from "@tanstack/react-query";

// Re-exported so callers keep importing the history translation from the queries module, while the
// pure (api-client-free) implementation lives on its own and stays unit-testable in the node project.
export { translateBackendMessages } from "./translateBackendMessages";

export const agentSessionMessagesOptions = (experiment_id: ExperimentId) =>
  api.queryOptions("get", "/experiments/{experiment_id}/agent-session/messages", {
    params: { path: { experiment_id } },
    queryKey: ["agent-session", experiment_id, "messages"]
  });

export const useAgentSessionMessages = (experiment_id: ExperimentId) =>
  useQuery({
    ...agentSessionMessagesOptions(experiment_id),
    enabled: Boolean(experiment_id),
    // Default `throwOnError: true` from queryClient would route 403 (feature flag) and 5xx
    // into the error boundary; we render an inline error state in the chat area instead.
    throwOnError: false,
    // Committed history only changes via agent turns (re-pulled explicitly on agent.turn.done)
    // and clear-session (which invalidates). Disable every automatic background refetch so
    // historyData is never mutated mid-turn: a focus/reconnect/mount refetch landing while a turn
    // streams would surface the already-committed user message alongside its optimistic live copy
    // in useAgentChat, rendering a duplicate bubble until the turn completes. With these off, the
    // only refetches are our coordinated invalidate (reconnect, after clearLocal) and fetchQuery
    // (turn.done, clear-after-fetch) — both sequence live-state clearing correctly. Listed
    // explicitly (rather than staleTime: Infinity) so the intent reads literally; the initial
    // load still fetches, and invalidate/fetchQuery still work since they bypass these flags.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false
  });

export const agentSessionDocumentsOptions = (experiment_id: ExperimentId) =>
  api.queryOptions("get", "/experiments/{experiment_id}/agent-session/documents", {
    params: { path: { experiment_id } },
    queryKey: ["agent-session", experiment_id, "documents"]
  });

export const useClearAgentSession = () =>
  api.useMutation("delete", "/experiments/{experiment_id}/agent-session", {
    onSuccess: async (_data, vars) => {
      await queryClient.invalidateQueries({
        queryKey: agentSessionMessagesOptions(vars.params.path.experiment_id).queryKey
      });
      // Clearing the session also deletes its documents, so the attachment slots are free again.
      await queryClient.invalidateQueries({
        queryKey: agentSessionDocumentsOptions(vars.params.path.experiment_id).queryKey
      });
    },
    onError: error => {
      notifications.error("Failed to clear conversation", error.message);
    }
  });

export const useSubmitSessionFeedback = () => api.useMutation("post", "/agent-proxy/feedback/");

// The multipart upload body is `FormData` (the openapi codegen represents the file list as a raw
// FormData), so callers build the form and append one `files` part per document. The whole draft is
// sent in a single request so the backend commits the documents atomically — all or none.
export const useUploadSessionDocument = () =>
  api.useMutation("post", "/experiments/{experiment_id}/agent-session/documents", {
    // Every upload path (the compose area and the landing-page bootstrap) must refresh the session
    // document list — the attachment slot count is derived from it.
    onSuccess: async (_data, vars) => {
      await queryClient.invalidateQueries({
        queryKey: agentSessionDocumentsOptions(vars.params.path.experiment_id).queryKey
      });
    }
  });
